import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, ArrowRight, Shield, Clock } from 'lucide-react';
import { sendFastIntakeEmail } from '../utils/emailjs';
interface FastTrackFormProps {
  onSubmit: () => void;
  onSkip: () => void;
}
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function FastTrackForm({
  onSubmit,
  onSkip
}: FastTrackFormProps) {
  const [formData, setFormData] = useState({
    birthDate: '',
    currentLocation: '',
    height: '',
    weight: '',
    medicalConditions: '',
    medicalConditionsDetails: '',
    allergies: '',
    allergiesDetails: '',
    currentMedications: '',
    currentMedicationsDetails: '',
    recentSurgeries: '',
    recentSurgeriesDetails: '',
    smoker: '',
    alcohol: '',
    recreationalDrugs: '',
    financingInterest: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [touched, setTouched] = useState({
    birthDate: false,
    currentLocation: false,
    height: false,
    weight: false
  });
  // CRITICAL: Submit guard to prevent double submission
  const isSendingRef = useRef(false);
  // City options
  const cityOptions = [{
    value: '',
    label: 'Selecciona tu ciudad'
  }, {
    value: 'Miami, FL',
    label: 'Miami, FL'
  }, {
    value: 'Los Angeles, CA',
    label: 'Los Angeles, CA'
  }, {
    value: 'Houston, TX',
    label: 'Houston, TX'
  }, {
    value: 'New York, NY',
    label: 'New York, NY'
  }, {
    value: 'Phoenix, AZ',
    label: 'Phoenix, AZ'
  }, {
    value: 'Las Vegas, NV',
    label: 'Las Vegas, NV'
  }, {
    value: 'Other City',
    label: 'Other City / Otra Ciudad'
  }];
  // Format height: user types digits, we format to X'Y"
  const formatHeight = (input: string) => {
    const digits = input.replace(/[^0-9]/g, '');
    if (digits.length === 0) return '';
    if (digits.length === 1) return `${digits}'`;
    if (digits.length === 2) {
      const feet = digits[0];
      const inches = digits[1];
      return `${feet}'${inches}"`;
    }
    if (digits.length >= 3) {
      const feet = digits[0];
      let inches = parseInt(digits.slice(1, 3));
      // Clamp inches to 0-11
      if (inches > 11) inches = 11;
      return `${feet}'${inches}"`;
    }
    return digits;
  };
  // Format weight: user types digits, we append lbs
  const formatWeight = (input: string) => {
    const digits = input.replace(/[^0-9]/g, '');
    return digits ? `${digits} lbs` : '';
  };
  // Format date to readable format (MM/DD/YYYY)
  const formatDate = (isoDate: string) => {
    if (!isoDate) return '';
    const date = new Date(isoDate);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🔵 SUBMIT CLICKED - Starting validation');
    // CRITICAL: Submit guard - prevent double submission
    if (isSendingRef.current) {
      console.log('⚠️ Submit blocked: already sending');
      return;
    }
    // Prevent double submission via state
    if (isSubmitting) {
      console.log('⚠️ Submit blocked: isSubmitting true');
      return;
    }
    // Clear previous errors
    setErrorMessage('');
    // Get lead data from localStorage (saved during main form submission)
    const leadData = JSON.parse(localStorage.getItem('dermaclinic_lead_data') || '{}');
    console.log('📦 Lead Data from localStorage:', leadData);
    // CRITICAL: Validate that Lead Form was completed first
    if (!leadData.fullName || !leadData.email || !leadData.phoneNumber) {
      console.log('❌ Lead data missing');
      setErrorMessage('⚠️ Primero debes completar el formulario principal. Por favor regresa y completa tus datos de contacto.');
      return;
    }
    // STRICT VALIDATION: ALL fields required (user already agreed to complete)
    const missingFields = [];
    if (!formData.birthDate) missingFields.push('Fecha de Nacimiento');
    if (!formData.currentLocation || formData.currentLocation === '' || formData.currentLocation === 'Selecciona tu ciudad') {
      missingFields.push('Ciudad');
    }
    if (!formData.height) missingFields.push('Estatura');
    if (!formData.weight) missingFields.push('Peso');
    if (!formData.medicalConditions) missingFields.push('Condiciones Médicas');
    if (formData.medicalConditions === 'Sí' && !formData.medicalConditionsDetails) {
      missingFields.push('Detalles de Condiciones Médicas');
    }
    if (!formData.allergies) missingFields.push('Alergias');
    if (formData.allergies === 'Sí' && !formData.allergiesDetails) {
      missingFields.push('Detalles de Alergias');
    }
    if (!formData.currentMedications) missingFields.push('Medicamentos');
    if (formData.currentMedications === 'Sí' && !formData.currentMedicationsDetails) {
      missingFields.push('Detalles de Medicamentos');
    }
    if (!formData.recentSurgeries) missingFields.push('Cirugías Recientes');
    if (formData.recentSurgeries === 'Sí' && !formData.recentSurgeriesDetails) {
      missingFields.push('Detalles de Cirugías');
    }
    if (!formData.smoker) missingFields.push('¿Fumas?');
    if (!formData.alcohol) missingFields.push('¿Consumes Alcohol?');
    if (!formData.recreationalDrugs) missingFields.push('¿Consumes Drogas Recreativas?');
    if (!formData.financingInterest) missingFields.push('Financiamiento');
    if (missingFields.length > 0) {
      console.log('❌ Missing fields:', missingFields);
      setErrorMessage(`⚠️ Por favor completa estos campos obligatorios: ${missingFields.join(', ')}`);
      // Scroll to top to show error
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      return;
    }
    console.log('✅ All validations passed - proceeding to send');
    // CRITICAL: Set guards
    isSendingRef.current = true;
    setIsSubmitting(true);
    try {
      // Check for duplicate submission within 2 minutes (anti rapid-fire only)
      const submissionKey = `dermaclinic_fastintake_sent_${leadData.email}_${leadData.phoneNumber}`;
      const lastSubmitTime = sessionStorage.getItem(submissionKey);
      const now = Date.now();
      const twoMinutes = 2 * 60 * 1000;
      if (lastSubmitTime) {
        const timeSinceLastSubmit = now - parseInt(lastSubmitTime);
        if (timeSinceLastSubmit < twoMinutes) {
          const secondsRemaining = Math.ceil((twoMinutes - timeSinceLastSubmit) / 1000);
          console.log(`⚠️ Fast Intake duplicate prevented. ${secondsRemaining} seconds remaining.`);
          setErrorMessage('Ya recibimos tu solicitud. Nuestro equipo te contactará pronto.');
          return;
        }
      }
      // Clean optional fields - accept common "no" variations and convert to friendly text
      const cleanOptionalField = (value: string) => {
        if (!value) return 'No especificado';
        const lowerValue = value.toLowerCase().trim();
        // Accept common "no" variations
        if (lowerValue === 'no' || lowerValue === 'n/a' || lowerValue === 'ninguna' || lowerValue === 'ninguno' || lowerValue === 'none' || lowerValue === 'nada') {
          return 'Ninguna';
        }
        return value;
      };
      // CRITICAL: DATA NORMALIZATION - Force all values to STRING
      // Use EXACT variable names that worked before (from successful email)
      const fastIntakeData = {
        // Lead identifiers (from main form) - FORCE STRING
        full_name: String(leadData.fullName || ''),
        email: String(leadData.email || ''),
        phone: String(leadData.phoneNumber || ''),
        city: String(leadData.city || ''),
        treatment: String(leadData.treatment || ''),
        whatsapp_consent: String(leadData.whatsappConsent ? 'yes' : 'no'),
        // Fast intake fields - USE EXACT NAMES FROM WORKING EMAIL
        dob: String(formatDate(formData.birthDate)),
        location: String(formData.currentLocation || ''),
        height: String(formData.height || '').replace(/"/g, '').replace(/'/g, '').trim(),
        weight: String(formData.weight || '').trim(),
        // Medical fields - simplified to "no" or details
        conditions: formData.medicalConditions === 'Sí' ? String(formData.medicalConditionsDetails || 'yes') : 'no',
        allergies: formData.allergies === 'Sí' ? String(formData.allergiesDetails || 'yes') : 'no',
        medications: formData.currentMedications === 'Sí' ? String(formData.currentMedicationsDetails || 'yes') : 'no',
        surgeries: formData.recentSurgeries === 'Sí' ? String(formData.recentSurgeriesDetails || 'yes') : 'no',
        // Lifestyle - lowercase
        smoking: String(formData.smoker || 'no').toLowerCase(),
        alcohol: String(formData.alcohol || 'no').toLowerCase(),
        drugs: String(formData.recreationalDrugs || 'no').toLowerCase(),
        // Financing
        financing: String(formData.financingInterest || 'no').toLowerCase(),
        // Metadata - FORCE STRING with ISO timestamp
        source: 'dermaclinic.blog',
        timestamp: String(new Date().toISOString())
      };
      // CRITICAL DEBUG: Log payload before sending
      console.log('📤 FAST_INTAKE_PAYLOAD:', JSON.stringify(fastIntakeData, null, 2));
      // Send Fast Intake email via EmailJS
      const response = await fetch('https://api.emailjs.com/api/v1.0/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          service_id: 'service_otq5unq',
          template_id: 'template_vc9z8rp',
          user_id: 'U3N2yFeaznDqrv0X6',
          template_params: fastIntakeData
        })
      });
      // CRITICAL DEBUG: Always log response status
      console.log('📥 FAST_INTAKE_HTTP_STATUS:', response.status, response.statusText);
      if (response.ok) {
        const result = await response.json();
        console.log('✅ FAST_INTAKE_SUCCESS:', result);
        // Mark this submission with current timestamp (2-minute protection)
        sessionStorage.setItem(submissionKey, now.toString());
        // Fire Meta Pixel FastIntakeCompleted Event ONLY after successful EmailJS send
        if (typeof window !== 'undefined' && typeof (window as any).fbq === 'function') {
          try {
            ;
            (window as any).fbq('trackCustom', 'FastIntakeCompleted', {
              content_name: leadData.treatment || '',
              city: leadData.city || '',
              lead_source: 'dermaclinic.blog'
            });
            console.log('PIXEL_FAST_INTAKE_FIRED', {
              treatment: leadData.treatment,
              city: leadData.city
            });
          } catch (fbqError) {
            console.warn('⚠️ Facebook Pixel error (non-critical):', fbqError);
          }
        }
        // Show visible success message
        setShowSuccess(true);
        // Clear form fields after success
        setFormData({
          birthDate: '',
          currentLocation: '',
          height: '',
          weight: '',
          medicalConditions: '',
          medicalConditionsDetails: '',
          allergies: '',
          allergiesDetails: '',
          currentMedications: '',
          currentMedicationsDetails: '',
          recentSurgeries: '',
          recentSurgeriesDetails: '',
          smoker: '',
          alcohol: '',
          recreationalDrugs: '',
          financingInterest: ''
        });
        // Call onSubmit after 2 seconds to return to Thank You page
        setTimeout(() => {
          onSubmit();
        }, 2000);
      } else {
        // CRITICAL DEBUG: Log full error details
        const errorBody = await response.text();
        console.error('❌ FAST_INTAKE_HTTP_STATUS:', response.status, response.statusText);
        console.error('❌ FAST_INTAKE_ERROR_BODY:', errorBody);
        console.error('❌ FAST_INTAKE_PAYLOAD_SENT:', JSON.stringify(fastIntakeData, null, 2));
        // User-friendly error message
        let userErrorMessage = 'Hubo un error al enviar tu información. ';
        if (response.status === 400) {
          userErrorMessage += 'Por favor verifica que todos los campos estén completos e intenta de nuevo.';
        } else if (response.status === 401 || response.status === 403) {
          userErrorMessage += 'Error de autenticación. Por favor intenta de nuevo.';
        } else if (response.status >= 500) {
          userErrorMessage += 'El servidor está temporalmente no disponible. Intenta en unos minutos.';
        } else {
          userErrorMessage += 'Por favor intenta de nuevo.';
        }
        setErrorMessage(userErrorMessage);
      }
    } catch (error) {
      console.error('❌ FAST_INTAKE_NETWORK_ERROR:', error);
      setErrorMessage('Error de conexión. Verifica tu internet e intenta de nuevo.');
    } finally {
      // CRITICAL: ALWAYS restore state in finally block
      isSendingRef.current = false;
      setIsSubmitting(false);
      console.log('🔓 Submit guard released');
    }
  };
  return <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="max-w-2xl mx-auto" data-id="element-319" data-mp="3b51nshqw2hum">
      <div className="bg-[#F7F4EF] rounded-2xl p-8 md:p-10 border-2 border-[#E6D3B3]" data-id="element-320" data-mp="164h2sg1x9yrv">
        {/* Success Message */}
        {showSuccess && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6 p-4 bg-green-50 border-2 border-green-500 rounded-lg text-center" data-id="element-321" data-mp="121kec8ppb8g0">
            <p className="text-sm font-semibold text-green-800" data-id="element-322" data-mp="nd7kbha6bzo5">
              ¡Listo! Recibimos tu información adicional. Te contactaremos muy
              pronto.
            </p>
          </motion.div>}

        {/* Error Message */}
        {errorMessage && <motion.div initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="mb-6 p-4 bg-red-50 border-2 border-red-500 rounded-lg text-center" data-id="element-323" data-mp="9ehkvuzvtnwm">
            <p className="text-sm font-semibold text-red-800" data-id="element-324" data-mp="2l11ukfko94mj">{errorMessage}</p>
          </motion.div>}

        {/* Header with Trust Signals */}
        <div className="flex items-start gap-4 mb-6" data-id="element-325" data-mp="2pyjwhm09ujl1">
          <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#C9A961]/10 flex items-center justify-center" data-id="element-326" data-mp="2u2jb1z1zr8br">
            <Clock className="w-6 h-6 text-[#C9A961]" strokeWidth={1.5} data-id="element-327" data-mp="1xda7qi1i9970" />
          </div>
          <div data-id="element-328" data-mp="22c528h8u4m6y">
            <h3 className="text-2xl font-bold text-[#2E2E2E] mb-2" data-id="element-329" data-mp="22blicuzmjnqa">
              Agiliza tu Consulta
            </h3>
            <p className="text-[#555555] text-sm leading-relaxed" data-id="element-330" data-mp="26akkk7tz16e1">
              Esta información permite a nuestros especialistas preparar tu
              evaluación médica con anticipación,
              <span className="font-semibold text-[#2E2E2E]" data-id="element-331" data-mp="1el9472dscur9">
                {' '}
                ahorrándote tiempo en tu cita
              </span>{' '}
              y asegurando que recibas recomendaciones personalizadas desde el
              primer día.
            </p>
          </div>
        </div>

        {/* Trust Badge */}
        <div className="flex items-center gap-2 mb-8 p-3 bg-white/50 rounded-lg border border-[#E6D3B3]/50" data-id="element-332" data-mp="2vnjwa7q4hgec">
          <Shield className="w-4 h-4 text-[#C9A961]" strokeWidth={1.5} data-id="element-333" data-mp="2qo6yirxpqowz" />
          <p className="text-xs text-[#888888]" data-id="element-334" data-mp="2qr2xmjlarjn0">
            <span className="font-semibold text-[#2E2E2E]" data-id="element-335" data-mp="230785t4bd0ix">
              100% confidencial.
            </span>{' '}
            Tu información es confidencial y se utiliza únicamente para preparar
            tu consulta.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-336" data-mp="2bxhmxogcb7sl">
          {/* Birth Date - REQUIRED */}
          <div data-id="element-337" data-mp="3nezg5u0aop0v">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-338" data-mp="3neprqauwepxa">
              Fecha de Nacimiento <span className="text-red-500" data-id="element-339" data-mp="3jdxel6iwo0vq">*</span>
            </label>
            <input type="date" value={formData.birthDate} onChange={e => {
            setFormData({
              ...formData,
              birthDate: e.target.value
            });
            setTouched({
              ...touched,
              birthDate: true
            });
          }} onBlur={() => setTouched({
            ...touched,
            birthDate: true
          })} className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-[#2E2E2E] focus:outline-none transition-colors ${touched.birthDate && !formData.birthDate ? 'border-red-400 focus:border-red-500' : formData.birthDate ? 'border-green-500 focus:border-green-600' : 'border-[#E6D3B3] focus:border-[#C9A961]'}`} style={{
            fontSize: '16px'
          }} data-id="element-340" data-mp="3jfqm0o1cep4s" />
            {touched.birthDate && !formData.birthDate && <p className="text-xs text-red-500 mt-1" data-id="element-341" data-mp="119ftkbshqwld">
                ⚠️ Este campo es requerido
              </p>}
            {formData.birthDate && <p className="text-xs text-green-600 mt-1" data-id="element-342" data-mp="2sigbp1iv4sr5">✓ Perfecto</p>}
          </div>

          {/* Current Location - REQUIRED - NOW DROPDOWN */}
          <div data-id="element-343" data-mp="2jjuupat1hlsh">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-344" data-mp="2jke3o20pozud">
              Ciudad <span className="text-red-500" data-id="element-345" data-mp="3g9vgd0y43m5k">*</span>
            </label>
            <select value={formData.currentLocation} onChange={e => {
            setFormData({
              ...formData,
              currentLocation: e.target.value
            });
            setTouched({
              ...touched,
              currentLocation: true
            });
          }} onBlur={() => setTouched({
            ...touched,
            currentLocation: true
          })} className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-[#2E2E2E] focus:outline-none transition-colors appearance-none cursor-pointer ${touched.currentLocation && !formData.currentLocation ? 'border-red-400 focus:border-red-500' : formData.currentLocation ? 'border-green-500 focus:border-green-600' : 'border-[#E6D3B3] focus:border-[#C9A961]'}`} style={{
            fontSize: '16px',
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23888888' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 1rem center'
          }} data-id="element-346" data-mp="3c80g7zrx3etd">
              {cityOptions.map(option => <option key={option.value} value={option.value} data-id="element-347" data-mp="f2esm5gocc8f">
                  {option.label}
                </option>)}
            </select>
            {touched.currentLocation && !formData.currentLocation && <p className="text-xs text-red-500 mt-1" data-id="element-348" data-mp="2o2f3t8u1bsee">
                ⚠️ Por favor selecciona tu ciudad
              </p>}
            {formData.currentLocation && <p className="text-xs text-green-600 mt-1" data-id="element-349" data-mp="2x3n2vt4dkx9j">✓ Perfecto</p>}
          </div>

          {/* Height & Weight - REQUIRED - IMPROVED UX */}
          <div className="grid grid-cols-2 gap-4" data-id="element-350" data-mp="36vj1r6eacret">
            <div data-id="element-351" data-mp="36upyifejck1f">
              <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-352" data-mp="36xcbiuk73rr1">
                Estatura <span className="text-red-500" data-id="element-353" data-mp="3bvxa6pjsu3dd">*</span>
              </label>
              <input type="text" inputMode="numeric" value={formData.height} onChange={e => {
              const formatted = formatHeight(e.target.value);
              setFormData({
                ...formData,
                height: formatted
              });
              setTouched({
                ...touched,
                height: true
              });
            }} onBlur={() => setTouched({
              ...touched,
              height: true
            })} placeholder="Ej: 56" className={`w-full px-4 py-3 bg-white border-2 rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none transition-colors ${touched.height && !formData.height ? 'border-red-400 focus:border-red-500' : formData.height ? 'border-green-500 focus:border-green-600' : 'border-[#E6D3B3] focus:border-[#C9A961]'}`} style={{
              fontSize: '16px'
            }} data-id="element-354" data-mp="3fw7hq6n8p1uc" />
              <p className="text-xs text-[#888888] mt-1" data-id="element-355" data-mp="1nyo19mgd0h1l">
                Solo números (ej: 56 = 5'6&quot;)
              </p>
              {touched.height && !formData.height && <p className="text-xs text-red-500 mt-1" data-id="element-356" data-mp="cfv6lnqfm45d">⚠️ Requerido</p>}
              {formData.height && <p className="text-xs text-green-600 mt-1" data-id="element-357" data-mp="ixep07guemfq">
                  ✓ {formData.height}
                </p>}
            </div>
            <div data-id="element-358" data-mp="3zcm7zhmy4zj">
              <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-359" data-mp="3zw6vnyo77h9">
                Peso <span className="text-red-500" data-id="element-360" data-mp="8xnwgbbuehia">*</span>
              </label>
              <div className="relative" data-id="element-361" data-mp="3rc6v8k1zffg4">
                <input type="text" inputMode="numeric" value={formData.weight.replace(' lbs', '')} onChange={e => {
                const formatted = formatWeight(e.target.value);
                setFormData({
                  ...formData,
                  weight: formatted
                });
                setTouched({
                  ...touched,
                  weight: true
                });
              }} onBlur={() => setTouched({
                ...touched,
                weight: true
              })} placeholder="Ej: 150" className={`w-full px-4 py-3 pr-12 bg-white border-2 rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none transition-colors ${touched.weight && !formData.weight ? 'border-red-400 focus:border-red-500' : formData.weight ? 'border-green-500 focus:border-green-600' : 'border-[#E6D3B3] focus:border-[#C9A961]'}`} style={{
                fontSize: '16px'
              }} data-id="element-362" data-mp="3rb59nlncbak5" />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#888888] text-sm font-medium pointer-events-none" data-id="element-363" data-mp="3lknwh2s58u5e">
                  lbs
                </span>
              </div>
              <p className="text-xs text-[#888888] mt-1" data-id="element-364" data-mp="3vewir8qkzqv4">Solo números</p>
              {touched.weight && !formData.weight && <p className="text-xs text-red-500 mt-1" data-id="element-365" data-mp="ef6836rgg4gu">⚠️ Requerido</p>}
              {formData.weight && <p className="text-xs text-green-600 mt-1" data-id="element-366" data-mp="if6natmdojoy">
                  ✓ {formData.weight}
                </p>}
            </div>
          </div>

          {/* Medical Conditions - NOW REQUIRED with YES/NO buttons */}
          <div data-id="element-367" data-mp="s85k2ys13gjn">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-368" data-mp="3rl6c1i4kauek">
              ¿Tienes Condiciones Médicas?{' '}
              <span className="text-red-500" data-id="element-369" data-mp="3vlzzybi0897g">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-370" data-mp="4ggpfbrqifzj">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => {
              setFormData({
                ...formData,
                medicalConditions: option.value,
                medicalConditionsDetails: option.value === 'No' ? '' : formData.medicalConditionsDetails
              });
            }} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${formData.medicalConditions === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-371" data-mp="1kugl2ajjrjgz">
                  {option.label}
                </button>)}
            </div>
            {!formData.medicalConditions && <p className="text-xs text-red-500 mt-1" data-id="element-372" data-mp="awqko1ctzai1">
                ⚠️ Por favor selecciona una opción
              </p>}
            {formData.medicalConditions === 'Sí' && <div className="mt-3" data-id="element-373" data-mp="5vt23nnwseg0">
                <input type="text" value={formData.medicalConditionsDetails} onChange={e => setFormData({
              ...formData,
              medicalConditionsDetails: e.target.value
            })} placeholder="Por favor especifica (ej: Diabetes, hipertensión)" className="w-full px-4 py-3 bg-white border-2 border-[#E6D3B3] rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-colors" style={{
              fontSize: '16px'
            }} data-id="element-374" data-mp="20dkv89vov4jh" />
                {!formData.medicalConditionsDetails && <p className="text-xs text-red-500 mt-1" data-id="element-375" data-mp="yoidqjwftfnn">
                    ⚠️ Por favor especifica tus condiciones
                  </p>}
              </div>}
          </div>

          {/* Allergies - NOW REQUIRED with YES/NO buttons */}
          <div data-id="element-376" data-mp="v4qcjruqss7g">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-377" data-mp="v8e5aosd5al4">
              ¿Tienes Alergias? <span className="text-red-500" data-id="element-378" data-mp="q9uhsjy4fqqn">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-379" data-mp="1548u61n6k1wc">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => {
              setFormData({
                ...formData,
                allergies: option.value,
                allergiesDetails: option.value === 'No' ? '' : formData.allergiesDetails
              });
            }} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${formData.allergies === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-380" data-mp="7itlk3ody9as">
                  {option.label}
                </button>)}
            </div>
            {!formData.allergies && <p className="text-xs text-red-500 mt-1" data-id="element-381" data-mp="3ddo40x0y5n9q">
                ⚠️ Por favor selecciona una opción
              </p>}
            {formData.allergies === 'Sí' && <div className="mt-3" data-id="element-382" data-mp="3n97w18ce41t7">
                <input type="text" value={formData.allergiesDetails} onChange={e => setFormData({
              ...formData,
              allergiesDetails: e.target.value
            })} placeholder="Por favor especifica (ej: Penicilina, látex)" className="w-full px-4 py-3 bg-white border-2 border-[#E6D3B3] rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-colors" style={{
              fontSize: '16px'
            }} data-id="element-383" data-mp="3n86ag9xqzwx8" />
                {!formData.allergiesDetails && <p className="text-xs text-red-500 mt-1" data-id="element-384" data-mp="1gentb0638e9d">
                    ⚠️ Por favor especifica tus alergias
                  </p>}
              </div>}
          </div>

          {/* Current Medications - NOW REQUIRED with YES/NO buttons */}
          <div data-id="element-385" data-mp="24bl48prwgfvi">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-386" data-mp="24bbft6mi6grx">
              ¿Tomas Medicamentos Actualmente?{' '}
              <span className="text-red-500" data-id="element-387" data-mp="1zc8hs64xxatg">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-388" data-mp="2wsdx05tap0ih">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => {
              setFormData({
                ...formData,
                currentMedications: option.value,
                currentMedicationsDetails: option.value === 'No' ? '' : formData.currentMedicationsDetails
              });
            }} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${formData.currentMedications === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-389" data-mp="2funrmaky8otz">
                  {option.label}
                </button>)}
            </div>
            {!formData.currentMedications && <p className="text-xs text-red-500 mt-1" data-id="element-390" data-mp="4z3yb1bwej2c">
                ⚠️ Por favor selecciona una opción
              </p>}
            {formData.currentMedications === 'Sí' && <div className="mt-3" data-id="element-391" data-mp="3r7dafvwswqaa">
                <input type="text" value={formData.currentMedicationsDetails} onChange={e => setFormData({
              ...formData,
              currentMedicationsDetails: e.target.value
            })} placeholder="Por favor especifica (ej: Aspirina, anticonceptivos)" className="w-full px-4 py-3 bg-white border-2 border-[#E6D3B3] rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-colors" style={{
              fontSize: '16px'
            }} data-id="element-392" data-mp="3r6byzqbvi1s1" />
                {!formData.currentMedicationsDetails && <p className="text-xs text-red-500 mt-1" data-id="element-393" data-mp="2kdfs0wfmy390">
                    ⚠️ Por favor especifica tus medicamentos
                  </p>}
              </div>}
          </div>

          {/* Recent Surgeries - NOW REQUIRED with YES/NO buttons */}
          <div data-id="element-394" data-mp="25o03zo7hobx0">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-395" data-mp="25ojbeazijhws">
              ¿Cirugías en los Últimos 2 Años?{' '}
              <span className="text-red-500" data-id="element-396" data-mp="33agyklou8bf2">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-397" data-mp="2yd73aperp5jb">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => {
              setFormData({
                ...formData,
                recentSurgeries: option.value,
                recentSurgeriesDetails: option.value === 'No' ? '' : formData.recentSurgeriesDetails
              });
            }} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${formData.recentSurgeries === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-398" data-mp="1hyp0w02o25fp">
                  {option.label}
                </button>)}
            </div>
            {!formData.recentSurgeries && <p className="text-xs text-red-500 mt-1" data-id="element-399" data-mp="q4si2tvqfnsq">
                ⚠️ Por favor selecciona una opción
              </p>}
            {formData.recentSurgeries === 'Sí' && <div className="mt-3" data-id="element-400" data-mp="2rvdnhx0zfdt8">
                <input type="text" value={formData.recentSurgeriesDetails} onChange={e => setFormData({
              ...formData,
              recentSurgeriesDetails: e.target.value
            })} placeholder="Por favor especifica (ej: Apendicectomía, cesárea)" className="w-full px-4 py-3 bg-white border-2 border-[#E6D3B3] rounded-lg text-[#2E2E2E] placeholder:text-[#AAAAAA] focus:outline-none focus:border-[#C9A961] transition-colors" style={{
              fontSize: '16px'
            }} data-id="element-401" data-mp="2rwf92vfmjip7" />
                {!formData.recentSurgeriesDetails && <p className="text-xs text-red-500 mt-1" data-id="element-402" data-mp="7i70wz8un8ie">
                    ⚠️ Por favor especifica las cirugías
                  </p>}
              </div>}
          </div>

          {/* Smoker - NOW REQUIRED */}
          <div data-id="element-403" data-mp="3bwop30sx9knk">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-404" data-mp="3buucqavpv0v3">
              ¿Fumas? <span className="text-red-500" data-id="element-405" data-mp="31rp0nhbomrie">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-406" data-mp="36ki9ohe6om0a">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Ocasionalmente',
              label: 'A veces'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => setFormData({
              ...formData,
              smoker: option.value
            })} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm ${formData.smoker === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-407" data-mp="3pht5tt4iqzbi">
                  {option.label}
                </button>)}
            </div>
            {!formData.smoker && <p className="text-xs text-red-500 mt-1" data-id="element-408" data-mp="vo6dy0nhcxk">
                ⚠️ Por favor selecciona una opción
              </p>}
          </div>

          {/* Alcohol - NOW REQUIRED */}
          <div data-id="element-409" data-mp="3n4z25lgmxeyu">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-410" data-mp="3n5h9pe8qzfm2">
              ¿Consumes Alcohol? <span className="text-red-500" data-id="element-411" data-mp="3j0re7qi0431o">*</span>
            </label>
            <div className="grid grid-cols-3 gap-3" data-id="element-412" data-mp="3e2g5jj3fzyl1">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Ocasionalmente',
              label: 'A veces'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => setFormData({
              ...formData,
              alcohol: option.value
            })} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm ${formData.alcohol === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-413" data-mp="1evj35dp78wmu">
                  {option.label}
                </button>)}
            </div>
            {!formData.alcohol && <p className="text-xs text-red-500 mt-1" data-id="element-414" data-mp="1auz8cgs3m53v">
                ⚠️ Por favor selecciona una opción
              </p>}
          </div>

          {/* Recreational Drugs - NOW REQUIRED */}
          <div data-id="element-415" data-mp="2m8tcq0hjzo3r">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-416" data-mp="2m8b567pfxngj">
              ¿Consumes Algún Tipo de Droga Recreativa?{' '}
              <span className="text-red-500" data-id="element-417" data-mp="32etztx7g103b">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-418" data-mp="32gn3d3mrf473">
              {[{
              value: 'No',
              label: 'No'
            }, {
              value: 'Sí',
              label: 'Sí'
            }].map(option => <button key={option.value} type="button" onClick={() => setFormData({
              ...formData,
              recreationalDrugs: option.value
            })} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium ${formData.recreationalDrugs === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-419" data-mp="3q6hglmazqurv">
                  {option.label}
                </button>)}
            </div>
            {!formData.recreationalDrugs && <p className="text-xs text-red-500 mt-1" data-id="element-420" data-mp="l8lgcl732emx">
                ⚠️ Por favor selecciona una opción
              </p>}
          </div>

          {/* Financing Interest - NOW REQUIRED */}
          <div className="pt-4 border-t-2 border-[#E6D3B3]" data-id="element-421" data-mp="becrq8l6b5u1">
            <label className="block text-sm font-semibold text-[#2E2E2E] mb-2" data-id="element-422" data-mp="3tlj21kkv5hjr">
              ¿Desea Verificar si Califica para Financiamiento de su
              Tratamiento? <span className="text-red-500" data-id="element-423" data-mp="3joeephfa7g1d">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3" data-id="element-424" data-mp="3jqip8i7ordam">
              {[{
              value: 'CareCredit',
              label: 'CareCredit'
            }, {
              value: 'Affirm',
              label: 'Affirm'
            }, {
              value: 'Alphaeon',
              label: 'Alphaeon Credit'
            }, {
              value: 'Cherry',
              label: 'Cherry'
            }, {
              value: 'Not Sure',
              label: 'No estoy seguro/a'
            }, {
              value: 'No',
              label: 'No necesito'
            }].map(option => <button key={option.value} type="button" onClick={() => setFormData({
              ...formData,
              financingInterest: option.value
            })} className={`px-4 py-3 rounded-lg border-2 transition-all font-medium text-sm ${formData.financingInterest === option.value ? 'bg-[#C9A961] border-[#C9A961] text-white' : 'bg-white border-[#E6D3B3] text-[#2E2E2E] hover:border-[#C9A961]'}`} data-id="element-425" data-mp="2zkvtewrxmmod">
                  {option.label}
                </button>)}
            </div>
            {!formData.financingInterest && <p className="text-xs text-red-500 mt-1" data-id="element-426" data-mp="1y13kit70b1v2">
                ⚠️ Por favor selecciona una opción
              </p>}
          </div>

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6" data-id="element-427" data-mp="26ydv0cc02myx">
            <button type="submit" disabled={isSubmitting || showSuccess} className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 bg-[#3A3A3A] text-white rounded-lg font-semibold hover:bg-[#1F1F1F] transition-colors shadow-lg hover:shadow-xl ${isSubmitting || showSuccess ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-428" data-mp="2kyomnhl4w5l5">
              {isSubmitting ? 'Enviando...' : showSuccess ? '¡Enviado!' : 'Enviar y Agilizar mi Consulta'}
              <ArrowRight className="w-5 h-5" strokeWidth={2} data-id="element-429" data-mp="2zmjhp4mm6m1e" />
            </button>
            <button type="button" onClick={onSkip} disabled={isSubmitting || showSuccess} className="flex-1 px-6 py-4 bg-transparent border-2 border-[#2E2E2E] text-[#2E2E2E] rounded-lg font-medium hover:bg-[#2E2E2E] hover:text-white transition-all" data-id="element-430" data-mp="36phlnx205owg">
              Completar Después
            </button>
          </div>

          <p className="text-xs text-center text-[#888888] leading-relaxed pt-2" data-id="element-431" data-mp="3qf0i5e0zrzb1">
            <span className="text-red-500" data-id="element-432" data-mp="3ufu25wbbctyn">*</span> Todos los campos son
            obligatorios.
            <br data-id="element-433" data-mp="3c5fojrm38z1" />
            Si seleccionas Sí en alguna pregunta médica, por favor proporciona
            detalles específicos.
          </p>
        </form>
      </div>
    </motion.div>;
}